<script setup lang="ts">
import { ref, watch } from "vue";
import { throttle } from "@zyzgroup/core-common";
import { __color, getCSSVar } from "@zyzgroup/core-web";
import Icon from "./Icon.vue";
import Loading11 from "../loading/Loading11.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Loading11 },
  inheritAttrs: true
});

export type ButtonStylePatternType = "fullfill" | "outline" | "transparent";

const props = withDefaults(
  defineProps<{
    label?: string;
    icon?: string;
    iconPosition?: "left" | "right";
    disabled?: boolean;
    width?: string;
    height?: string;
    padding?: string;
    fontSize?: string;
    fontWeight?: string;
    borderRadius?: string; // border-radius:x/y
    stylePattern?: ButtonStylePatternType;
    fontColor?: string;
    borderColor?: string;
    bgColor?: string;
    bgColorStart?: string; // 渐变背景色-开始
    bgColorEnd?: string; // 渐变背景色-结束
    asyncClick?: () => Promise<void>;
  }>(),
  {
    iconPosition: "left",
    disabled: false,
    width: "auto",
    height: "44px",
    padding: "20px",
    fontSize: "16px",
    fontWeight: "normal", // normal bold
    borderRadius: "4px",
    stylePattern: "fullfill"
  }
);

const emits = defineEmits<{
  (event: "click", e: Event): void;
  (event: "throttleClick", e: Event): void; // for ce element
}>();

const el = ref<HTMLElement>();

const xgBlue = getCSSVar("--yz-xg-blue", "#5a82f0");

const fontColor = ref(props.fontColor);
const borderColor = ref(props.borderColor);
const bgColor = ref(props.bgColor);

const baseFontColor = ref(fontColor.value);
const baseBorderColor = ref(borderColor.value);
const baseBgColor = ref(bgColor.value);
const baseBgColorStart = ref(props.bgColorStart || "");
const baseBgColorEnd = ref(props.bgColorEnd || "");

const isBgColorGradient = props.bgColorStart && props.bgColorEnd;

function setStylePattern(stylePattern: ButtonStylePatternType) {
  switch (stylePattern) {
    case "transparent":
      borderColor.value = props.borderColor || "rgba(255,255,255,0)";
      bgColor.value = props.bgColor || "rgba(255,255,255,0)";
      fontColor.value = props.fontColor || xgBlue;
      break;
    case "outline":
      borderColor.value = props.borderColor || xgBlue;
      bgColor.value = props.bgColor || "rgba(255,255,255,0)";
      fontColor.value = props.fontColor || xgBlue;
      break;
    case "fullfill":
    default:
      borderColor.value = props.borderColor || "rgba(255,255,255,0)";
      bgColor.value = props.bgColor || xgBlue;
      fontColor.value =
        props.fontColor || __color.set(bgColor.value).opposite(true).toString();
      break;
  }
  baseFontColor.value = fontColor.value;
  baseBorderColor.value = borderColor.value;
  baseBgColor.value = bgColor.value;
  // 背景色-渐变
  if (isBgColorGradient) {
    baseBgColorStart.value = props.bgColorStart;
    baseBgColorEnd.value = props.bgColorEnd;
    bgColor.value = `linear-gradient(180deg, ${baseBgColorStart.value} 0%, ${baseBgColorEnd.value} 100%)`;
  }
}

watch(
  () => props.stylePattern,
  () => {
    setStylePattern(props.stylePattern);
  },
  { immediate: true }
);

const opacity = ref(props.disabled ? "0.6" : "1");
const cursor = ref(props.disabled ? "not-allowed" : "pointer");

let isMouseOver = false;
const darkenOver = 0.03;
const darkenClick = 0.06;

const mouseover = () => {
  if (props.disabled) return;
  isMouseOver = true;
  if (props.stylePattern == "transparent") {
    fontColor.value = __color
      .set(baseFontColor.value)
      .darken(darkenOver)
      .toString();
  }
  borderColor.value = __color
    .set(baseBorderColor.value)
    .darken(darkenOver)
    .toString();
  // 背景色-渐变
  if (isBgColorGradient) {
    bgColor.value = `linear-gradient(180deg, ${baseBgColorEnd.value} 0%, ${baseBgColorStart.value} 100%)`;
  } else {
    bgColor.value = __color
      .set(baseBgColor.value)
      .darken(darkenOver)
      .toString();
  }
};

const mouseleave = () => {
  if (props.disabled) return;
  isMouseOver = false;
  fontColor.value = baseFontColor.value;
  borderColor.value = baseBorderColor.value;
  // 背景色-渐变
  if (isBgColorGradient) {
    bgColor.value = `linear-gradient(180deg, ${baseBgColorStart.value} 0%, ${baseBgColorEnd.value} 100%)`;
  } else {
    bgColor.value = baseBgColor.value;
  }
};

const asyncClickPending = ref(false);

const throttleClick = throttle((e: Event) => {
  if (props.disabled || asyncClickPending.value) {
    return;
  }
  if (props.stylePattern == "transparent") {
    fontColor.value = __color
      .set(baseFontColor.value)
      .darken(darkenClick)
      .toString();
  }
  borderColor.value = __color
    .set(baseBorderColor.value)
    .darken(darkenClick)
    .toString();
  // 背景色-渐变
  if (isBgColorGradient) {
    bgColor.value = `linear-gradient(180deg, ${baseBgColorStart.value} 0%, ${baseBgColorEnd.value} 100%)`;
  } else {
    bgColor.value = __color
      .set(baseBgColor.value)
      .darken(darkenClick)
      .toString();
  }
  setTimeout(() => {
    if (isMouseOver) {
      mouseover();
    } else {
      mouseleave();
    }
  }, 150);
  emits("click", e);
  emits("throttleClick", e);
  if (props.asyncClick) {
    asyncClickPending.value = true;
    props
      .asyncClick()
      .then(() => {
        asyncClickPending.value = false;
      })
      .catch(() => {
        asyncClickPending.value = false;
      });
  }
}, 300);

defineExpose({
  // 父组件ref子Button组件时：xxx.value.el.getBoundingClientRect()
  el
});
</script>

<template>
  <div
    ref="el"
    class="button"
    :style="[
      `opacity: ${opacity}`,
      `border: 1px solid ${borderColor}`,
      `background: ${bgColor}`,
      `color: ${fontColor}`,
      `border-radius: ${props.borderRadius}`,
      `width: ${props.width}`,
      `height: ${props.height}`,
      `line-height: ${props.height}`,
      `font-size: ${props.fontSize}`,
      `cursor: ${cursor}`,
      `user-select: none`,
      `box-sizing: border-box`,
      `display: inline-flex`,
      `justify-content: center`,
      `align-items: center`,
      `z-index: 0`
    ]"
    @mouseover="mouseover"
    @mouseleave="mouseleave"
    @click="throttleClick"
  >
    <label
      class="label truncate"
      :style="{
        padding: '0 ' + padding,
        cursor: cursor,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: props.fontWeight || 'normal'
      }"
    >
      <div
        v-if="asyncClickPending"
        class="loading"
        style="margin-right: 6px"
      >
        <Loading11 :color="fontColor"></Loading11>
      </div>
      <slot>
        <Icon
          v-if="icon && iconPosition === 'left'"
          :icon="icon"
          :color="fontColor"
          :style="{
            marginRight: '4px',
            cursor: cursor
          }"
        ></Icon>
        <span
          :style="{
            cursor: cursor
          }"
        >
          {{ label }}
        </span>
        <Icon
          v-if="icon && iconPosition === 'right'"
          :icon="icon"
          :color="fontColor"
          :style="{
            marginLeft: '4px',
            cursor: cursor
          }"
        ></Icon>
      </slot>
    </label>
  </div>
</template>
